import { Link } from "@tanstack/react-router"
import type { PimWorkflowList } from "@nova-ai/shared"
import { formatDateTime } from "../../lib/format-date"
import type { WorkflowHistorySearch } from "../../services/use-pim-workflow-history"
import { Button } from "../ui/button"
import { StatusBadge } from "../ui/status-badge"
import { Table } from "../ui/table"
import { workflowKindLabel } from "./workflow-history-presentation"
import { WorkflowCounts } from "./workflow-history-evidence"

export function WorkflowHistoryList({
  data,
  search,
  onChange,
}: {
  data: PimWorkflowList
  search: WorkflowHistorySearch
  onChange: (search: WorkflowHistorySearch) => void
}) {
  return (
    <section aria-label="Workflow receipts" className="space-y-3">
      <Table
        rows={data.receipts}
        rowKey={(receipt) => receipt.id}
        stackOnMobile
        empty="No receipts match these filters. Clear filters to include unresolved receipts, or refresh later."
        columns={[
          {
            key: "received",
            header: "Received",
            render: (receipt) => (
              <div className="space-y-1">
                <Link
                  to="/studio/history"
                  search={{
                    ...search,
                    receiptId: receipt.id,
                    targetAfter: undefined,
                    attemptAfter: undefined,
                  }}
                  className="rounded text-info hover:underline focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Receipt #{receipt.id}
                </Link>

                <p className="text-xs text-muted-foreground">
                  {formatDateTime(receipt.receivedAt)}
                </p>

                <div className="flex flex-wrap gap-1">
                  {receipt.coverage !== "instrumented" && (
                    <StatusBadge>
                      {receipt.coverage === "expired" ? "Expired evidence" : "Incomplete history"}
                    </StatusBadge>
                  )}

                  {receipt.summary.failed && (
                    <StatusBadge tone="destructive">Unresolved failures</StatusBadge>
                  )}

                  {receipt.deliveryResolution && (
                    <StatusBadge tone="success">
                      Present image obligation resolved later
                    </StatusBadge>
                  )}

                  {receipt.summary.pending && <StatusBadge tone="info">Pending work</StatusBadge>}

                  {receipt.summary.unknown && <StatusBadge>Unknown evidence</StatusBadge>}
                </div>
              </div>
            ),
          },
          {
            key: "product",
            header: "Product / SKU",
            render: (receipt) => (
              <div className="space-y-1">
                <p>Product {receipt.productId ?? "unresolved"}</p>

                <p className="break-all font-mono text-xs">{receipt.sku ?? "SKU unresolved"}</p>

                {receipt.skuCount > 1 && (
                  <p className="text-xs text-muted-foreground">
                    +{receipt.skuCount - 1} other SKUs
                  </p>
                )}

                {receipt.validation === "rejected" && (
                  <StatusBadge tone="destructive">Validation rejected</StatusBadge>
                )}
              </div>
            ),
          },
          {
            key: "kind",
            header: "Kind",
            render: (receipt) => (
              <div>
                <p>{workflowKindLabel(receipt.kind)}</p>

                <p className="mt-1 text-xs text-muted-foreground">
                  All {receipt.summary.scopes} targets
                </p>

                <p className="text-xs text-muted-foreground">
                  {receipt.summary.matchingScopes} match scope
                </p>
              </div>
            ),
          },
          {
            key: "generation",
            header: "Generation",
            render: (receipt) => <WorkflowCounts counts={receipt.summary.generation} />,
          },
          {
            key: "upload",
            header: "GCS upload",
            render: (receipt) => <WorkflowCounts counts={receipt.summary.upload} />,
          },
          {
            key: "publication",
            header: "Pub/Sub acceptance",
            render: (receipt) => <WorkflowCounts counts={receipt.summary.publication} />,
          },
        ]}
      />

      <div className="flex flex-wrap gap-2">
        {search.before && (
          <Button onClick={() => onChange({ ...search, before: undefined })}>
            Newest receipts
          </Button>
        )}

        {data.nextCursor && (
          <Button onClick={() => onChange({ ...search, before: data.nextCursor ?? undefined })}>
            Older receipts
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Stage counts cover every linked target, including targets outside your filters.
      </p>
    </section>
  )
}
